import {
  getEventAddOnId,
  getEventAddOnName,
  getEventAddOnUnitPriceAmount,
  getEventAddOnPriceType,
} from './eventAddOnGetters'
import type {
  Cart,
  CartItem,
  GiftcardCartItem,
  EventDateCartItem,
  ShippingCost,
  GiftcardAddon,
} from '@/composables/types/cart'
import type {
  Coupon,
  GTMItem,
  OrderCreationItem,
  AgnosticTotals,
  OrderCreationAddonItem,
  OrderCreationEventItem,
  OrderCreationGiftCardItem,
} from '@/composables/types/checkout'
import type { EventAddOn, Price } from '~/composables/types/event'
import {
  buildEventCheckoutLine,
  buildGiftCardCheckoutLine,
} from '~/utils/checkoutLinePayload'
import { KONFETTI_GIFTCARD_SUPPLIER_ID } from '~/utils/konfettiCheckoutConstants'

export interface GetOrderLineItemsOptions {
  external?: boolean
  /** When `external`, default supplier for gift cards without `supplierId` on the line. */
  bookingGiftcardSupplierFallback?: string
  includeSupplierIds?: boolean
}

interface IGiftboxPrice {
  amount: number
  currency: string
  formatted: string
}

const fnGetItemValidationRemovableReasons = (): string[] => {
  return ['notAvailable', 'notFound', 'soldOut', 'minAdvance', 'maxAdvance']
}

const fnGetCartItemsLength = (cart: Cart): number => {
  return cart.items.length
}

const getGiftboxPrice = (cart: Cart): IGiftboxPrice => {
  let returnObj: IGiftboxPrice = {
    amount: 100,
    currency: '',
    formatted: '',
  }

  if (cart?.giftboxPrice) {
    returnObj = { ...cart?.giftboxPrice }
  }

  return {
    ...returnObj,
    amount: Number(returnObj.amount) / 100,
  }
}

const getGiftboxPriceAmount = (cart: Cart): string =>
  cart?.giftboxPrice?.amount ?? 0

const getShippingCost = (cart: Cart): ShippingCost | null => {
  return cart?.shippingCost || null
}

const getShippingPrice = (cart: Cart): Price | null => {
  return getShippingCost(cart)?.value || null
}

const getTotalItems = (cart: Cart): number => {
  return cart?.items?.length || 0
}

const getDiscount = (
  coupon: Coupon,
  cartTotal: number,
  totalWithoutPackagingAndShipping: number
): number => {
  if (!coupon) return 0
  const amountToWhichDiscountShouldBeApplied =
    coupon?.coupon_type === 'MARKETING'
      ? totalWithoutPackagingAndShipping
      : cartTotal

  const minimumDiscount = coupon.minimum_order_value
  const minimumAmount = minimumDiscount
    ? Number(minimumDiscount.amount) / 100
    : 0

  // Check minimum order value
  if (minimumDiscount && amountToWhichDiscountShouldBeApplied < minimumAmount) {
    return 0
  }

  let discount = 0
  if (coupon.discount_type === 'FIXED_VALUE') {
    discount = Number((coupon.value as Price).amount) / 100
  } else if (coupon.discount_type === 'PERCENTAGE_VALUE') {
    discount =
      (amountToWhichDiscountShouldBeApplied * Number(coupon.value)) / 100
  }

  const maximumDiscount = coupon.maximum_discount_amount
  const maximumDiscountAmount = maximumDiscount
    ? Number(maximumDiscount.amount) / 100
    : 0

  // Apply maximum discount amount limit
  if (maximumDiscount && discount > maximumDiscountAmount) {
    discount = Number(coupon.maximum_discount_amount.amount) / 100
  }

  return discount
}

const getItems = (cart: Cart): CartItem[] => {
  return cart?.items || []
}

const getItemPermalink = (item: CartItem): string => {
  return item.permalink || ''
}

const getItemId = (item: CartItem): string => {
  return item.id || ''
}

const getGiftcardId = (item: CartItem): string => {
  return item.giftcardId || item.id || ''
}

const getEventDescriptionId = (item: CartItem): string => {
  return item.eventDescriptionId || ''
}

const getItemIdForGtm = (item: CartItem): string => {
  // for GA we want the event description ID's
  if (item.type === 'GIFT_CARD' && !getEventDescriptionId(item)) {
    const price = getUnitItemPrice(item)
    return 'giftcard-' + price
  }
  return getEventDescriptionId(item) || ''
}

const isGiftcard = (item: CartItem): boolean => {
  return item.type === 'GIFT_CARD'
}

export const isValueBasedGiftcard = (item: CartItem): boolean => {
  return item?.type === 'GIFT_CARD' && !item?.id && !item.eventDescriptionId
}

const getGiftcardType = (item: CartItem): string | undefined => {
  return item?.giftcardType || undefined
}

const hasGiftcard = (cart: Cart): boolean => {
  const length = cart.items.length

  for (let i = 0; i < length; i++) {
    if (cart.items[i].type === 'GIFT_CARD') {
      return true
    }
  }

  return false
}

const getItemsForGtm = (cart: Cart): GTMItem[] => {
  const items = cart.items.flatMap((item) => {
    const price = getUnitItemPrice(item)
    const mainItems: Omit<GTMItem, 'index'>[] = []

    if (item.type === 'GIFT_CARD') {
      mainItems.push({
        item_id: getItemIdForGtm(item),
        item_name: item.title,
        item_category: getItemMainCategory(item),
        item_variant: getItemVariantForGtm(item),
        item_brand: item.supplier || 'konfetti',
        quantity: item.quantity,
        price: price?.toFixed(2) || price,
        ship: getGiftcardAddon(item) !== 'PDF',
        shipType: getGiftcardAddon(item) !== 'PDF' && getGiftcardAddon(item),
        google_business_vertical: 'custom',
      })
    } else {
      const pricingCategories = item.pricings?.price_breakdown?.filter(
        (pricing) => pricing.total_participants > 0
      )
      pricingCategories.forEach((pricing) => {
        mainItems.push({
          item_id: getItemIdForGtm(item),
          item_name: item.title,
          item_category: getItemMainCategory(item),
          item_variant: getItemVariantForGtm(item, pricing.title),
          item_brand: item.supplier,
          quantity: pricing.total_participants,
          price:
            (Number(pricing.price_per_person?.amount) / 100).toFixed(2) ||
            '0.00',
          google_business_vertical: 'custom',
          ...(item.instantBooking === true
            ? { item_category5: 'request-to-book' }
            : {}),
        })
      })
    }

    const addon = getItemAddon(item)
    const addonItems: Omit<GTMItem, 'index'>[] = []

    if (addon) {
      const addons = Array.isArray(addon) ? addon : [addon]

      addons.forEach((addon) => {
        const addonId = getEventAddOnId(addon)
        const addonName = getEventAddOnName(addon)
        const addonPriceAmount = getEventAddOnUnitPriceAmount(addon)
        const addonPriceType = getEventAddOnPriceType(addon)

        if (
          addonId &&
          addonName &&
          addonPriceAmount !== null &&
          addonPriceAmount !== undefined
        ) {
          const addonPrice = Number(addonPriceAmount) / 100
          addonItems.push({
            item_id: `addon-${addonId}`,
            item_name: addonName,
            item_category: 'ADD_ON',
            item_variant: addonPriceType || 'ADD_ON',
            quantity: addonPriceType === 'PER_SESSION' ? 1 : item.quantity,
            price: addonPrice.toFixed(2),
            google_business_vertical: 'custom',
          })
        }
      })
    }

    return [...mainItems, ...addonItems]
  })

  return items.map((item, index) => ({ ...item, index }))
}

const getItemMainCategory = (item: CartItem): string => {
  if (item.type === 'GIFT_CARD') {
    return 'giftcard'
  }
  return item.category || ''
}

const getItemVariantForGtm = (
  item: CartItem,
  ticketCategory?: string
): string => {
  if (item.attendanceMode?.toUpperCase() === 'NATIONWIDE') {
    return 'KIT'
  }

  if (item.type === 'GIFT_CARD') {
    return item.type
  }

  if (item.private === true) {
    return 'PRIVATE_EVENT'
  }

  if (item.type === 'EVENT') {
    return ticketCategory ? `EVENT_${ticketCategory}` : item.type
  }

  // default will be "EVENT"
  return item.type
}

const getCartHasItem = (cart: Cart, id: string): boolean => {
  return cart.items.some((item: CartItem) => item.id === id)
}

const getCartHasBoxEvent = (cart: Cart): boolean => {
  return cart.items.some(
    (item: CartItem) => item?.attendanceMode === 'NATIONWIDE'
  )
}

const hasPackagingCosts = (cart: Cart): boolean => {
  return cart.items.some((item: CartItem) =>
    ['CARD', 'BOX'].includes((item as GiftcardCartItem)?.giftcardAddon)
  )
}

const hasShippingCosts = (cart: Cart): boolean => {
  return (
    cart.items.some((item: CartItem) => item?.hasShippingCosts) ||
    hasPackagingCosts(cart)
  )
}

const hasEventAnyEventThatRequiresAddress = (cart: Cart): boolean => {
  return cart.items.some((item: CartItem) => {
    if (item?.requiredAddress) return true
    if (!item?.addOn) return false
    const addOns = Array.isArray(item.addOn) ? item.addOn : [item.addOn]
    return addOns.some((addOn) => addOn?.ship)
  })
}

const requiresShippingAddress = (cart: Cart): boolean => {
  return hasEventAnyEventThatRequiresAddress(cart) || hasPackagingCosts(cart)
}

const getItemName = (item: CartItem): string => {
  return item.title || ''
}

const getItemImage = (item: CartItem): string => {
  return item.thumbnail || ''
}

const getItemAttendanceMode = (item: CartItem): string | undefined => {
  return item.attendanceMode || undefined
}

const getItemPrice = (item: CartItem): number => {
  const privateMinTickets = Number(item?.privateMinTickets) || 1
  const qty =
    item.private && privateMinTickets > item.quantity
      ? item.privateMinTickets
      : item.quantity

  if (!qty) return 0

  const price = item?.price?.amount || item?.value

  return (Number(price) * qty) / 100
}

const getItemPricingTotalPrice = (item: CartItem): number | undefined => {
  if (!item.pricings) return undefined
  return Number(item.pricings?.price?.total_price?.amount) / 100
}

const getItemPricingBasePrice = (item: CartItem): number | undefined => {
  if (!item.pricings) return undefined
  return Number(item.pricings?.price?.base_price?.amount) / 100
}

const getItemPricingAppliedDiscount = (item: CartItem): number | undefined => {
  if (!item.pricings) return undefined
  return Number(item.pricings?.price?.applied_discount?.amount) / 100
}

export const getAllAddonsForOrder = (cart: Cart): OrderCreationAddonItem[] => {
  return cart.items
    .filter((item) => item.addOn)
    .map((item) => {
      const addOns = Array.isArray(item.addOn) ? item.addOn : [item.addOn]
      const parentPrivate =
        item.type === 'EVENT' ? { private: Boolean(item.private) } : {}
      return addOns.map((addOn) => ({
        addon: addOn.id,
        type: 'ADD_ON_SETTING',
        quantity:
          getEventAddOnPriceType(addOn) === 'PER_SESSION' ? 1 : item.quantity,
        parent: item.id,
        ...parentPrivate,
      }))
    })
    .flat()
}

const getFirstAddOnName = (cart: Cart): string | undefined => {
  const firstItemWithAddOn = cart?.items?.find((item) => item?.addOn)
  if (!firstItemWithAddOn?.addOn) return undefined
  const addOn = firstItemWithAddOn.addOn
  const first = Array.isArray(addOn) ? addOn[0] : addOn
  return first?.name
}

const getAddOnPrice = (addOn: EventAddOn, quantity: number): number => {
  if (!quantity) return 0

  const addOnPrice = Number(addOn?.unit_price?.amount)

  if (!addOnPrice) return 0

  const qty = getEventAddOnPriceType(addOn) === 'PER_SESSION' ? 1 : quantity

  return (addOnPrice * qty) / 100
}

const getTotalAddOnPrice = (cart: Cart) =>
  cart?.items.reduce((total, item) => {
    if (!item?.addOn) return total
    const addOns = Array.isArray(item.addOn) ? item.addOn : [item.addOn]
    return addOns.reduce((sum, addOn) => {
      return sum + getAddOnPrice(addOn, item.quantity)
    }, total)
  }, 0)

const getUnitItemPrice = (item: CartItem): number => {
  // For private events, always use price_per_person from price_breakdown
  // because total_price represents the minimum tickets, not actual quantity
  if (
    item?.dateTypeToBeBooked === 'PRIVATE' &&
    item.pricings?.price_breakdown?.[0]?.price_per_person?.amount
  ) {
    return (
      Number(item.pricings.price_breakdown[0].price_per_person.amount) / 100
    )
  }

  // For non-private events, calculate from total_price (includes promotions)
  const totalPrice = Number(item.pricings?.price.total_price.amount || '0')
  const qty = item?.quantity

  return totalPrice / qty / 100
}

const getItemPriceAmount = (item: CartItem): number => {
  return Number(
    item.pricings?.price_breakdown[0].price_per_person.amount || '0'
  )
}

const getItemTotalPrice = (item: CartItem): number => {
  let total = getItemPricingTotalPrice(item) || 0

  // Add addon prices
  if ((item as EventDateCartItem)?.addOn) {
    const addOns = Array.isArray(item.addOn) ? item.addOn : [item.addOn]
    addOns.forEach((addOn) => {
      total += getAddOnPrice(addOn, item.quantity)
    })
  }

  // Add giftcard addon price if applicable
  if (isGiftcard(item)) {
    total += getGiftcardAddonTotalPrice(item as GiftcardCartItem)
  }

  return total
}

const getItemQty = (item: CartItem): number => {
  return item.quantity || 1
}

const getAvailableStock = (item: CartItem): number => {
  if (item.type === 'GIFT_CARD') {
    return 999999
  }

  if (item.private) {
    return item?.privateMaxTickets || item.availableTicketsQuantity || 1
  }

  return item.availableTicketsQuantity || 1
}

const isPrivate = (item: CartItem): boolean => {
  return item.private || false
}

const getTotals = (cart: Cart): AgnosticTotals => {
  return {
    total: getCartTotalPrice(cart, true, null),
    subtotal: getCartTotalPrice(cart, false, null),
    special: undefined,
  }
}

const getItemAddon = (
  cartItem: CartItem
): EventAddOn | EventAddOn[] | undefined => {
  return cartItem?.addOn
}

// get label of selected giftcard addon
const getGiftcardAddon = (
  cartItem: GiftcardCartItem
): GiftcardAddon | undefined => {
  return cartItem?.giftcardAddon
}

// map of available addons with legacy option
const getGiftcardAddonList = [
  {
    id: 'PDF',
    price: 0,
    image: '/_nuxt3-static/giftcard/giftcard-pdf.png',
    is_visible: true,
  },
  {
    id: 'CARD',
    price: 295,
    image: '/_nuxt3-static/giftcard/envelope.png',
    is_visible: true,
  },
  {
    id: 'BOX',
    price: 695,
    image: '/_nuxt3-static/giftcard/box-general.png',
    is_visible: true,
  },
]

// get price of giftcard addon using giftcard label
const getGiftcardAddonSinglePrice = (item: GiftcardCartItem): number => {
  const addon = getGiftcardAddon(item)
  const addonItem = getGiftcardAddonList.find((item) => item.id === addon)
  if (!addonItem) {
    return 0
  }
  return addonItem?.price || 0
}

// calculate total giftcard addon price for single card
const getGiftcardAddonTotalPrice = (item: GiftcardCartItem): number => {
  const addonPrice = getGiftcardAddonSinglePrice(item)
  if (addonPrice === 0) return 0

  // formatted price of single item, like 2.95 instead of 295
  const amount = addonPrice / 100

  // if giftcard is value based or split is enabled, multiply by quantity of added cards
  const shouldMultiplyByQuantity =
    isValueBasedGiftcard(item) || item.split === true
  return shouldMultiplyByQuantity ? amount * item.quantity : amount
}

const getTotalGiftboxPriceInCart = (cart: Cart): number => {
  return cart.items.reduce((total, item) => {
    if (!isGiftcard(item)) return total

    return total + Number(getGiftcardAddonTotalPrice(item as GiftcardCartItem))
  }, 0)
}

const getCartTotalPrice = (
  cart: Cart,
  _isTotal: boolean,
  voucherData: Coupon | null = null,
  userBalance: number | null = null,
  deliveryCost: number | null = null
): number => {
  if (!cart || !cart.items) {
    return 0
  }

  let total = 0
  let totalWithoutPackagingAndShipping = 0
  for (let i = 0; i < cart?.items?.length; i++) {
    const item = cart.items[i]

    total += getItemPricingTotalPrice(item) || 0
    totalWithoutPackagingAndShipping += getItemPricingTotalPrice(item) || 0

    if ((item as EventDateCartItem)?.addOn) {
      if (Array.isArray(item.addOn)) {
        item.addOn.forEach((addOn) => {
          total += getAddOnPrice(addOn, item.quantity)
          totalWithoutPackagingAndShipping += getAddOnPrice(
            addOn,
            item?.quantity
          )
        })
      } else {
        total += getAddOnPrice(item.addOn, item.quantity)
        totalWithoutPackagingAndShipping += getAddOnPrice(
          item.addOn,
          item?.quantity
        )
      }
    }

    if (isGiftcard(item)) {
      total += getGiftcardAddonTotalPrice(item as GiftcardCartItem)
    }
  }

  // delivery cost
  if (deliveryCost && _isTotal) {
    total += deliveryCost
  }

  if (voucherData && _isTotal) {
    total = Math.max(
      0,
      total - getDiscount(voucherData, total, totalWithoutPackagingAndShipping)
    )
  }

  if (userBalance && _isTotal) {
    total = Math.max(0, total - userBalance)
  }

  // cover the corner cases of JS mathematics, like 0.1 + 0.2 = 0.30000000000000004
  // using + to convert to number
  return +total.toFixed(2)
}

export const getTotalWithoutPackagingAndShipping = (cart: Cart): number => {
  if (!cart || !cart.items) {
    return 0
  }

  let totalWithoutPackagingAndShipping = 0
  for (let i = 0; i < cart?.items?.length; i++) {
    const item = cart.items[i]
    totalWithoutPackagingAndShipping += getItemPricingTotalPrice(item) || 0

    if ((item as EventDateCartItem)?.addOn) {
      if (Array.isArray(item.addOn)) {
        item.addOn.forEach((addOn) => {
          totalWithoutPackagingAndShipping += getAddOnPrice(
            addOn,
            item?.quantity
          )
        })
      } else {
        totalWithoutPackagingAndShipping += getAddOnPrice(
          item.addOn,
          item?.quantity
        )
      }
    }
  }
  return totalWithoutPackagingAndShipping
}

export const getItemsForOrder = (
  cart: Cart,
  options?: GetOrderLineItemsOptions
): OrderCreationItem[] => {
  const length = cart?.items?.length
  const items: OrderCreationItem[] = []
  const external = Boolean(options?.external)
  const bookingGiftcardSupplierFallback =
    options?.bookingGiftcardSupplierFallback
  const includeSupplierIds = Boolean(options?.includeSupplierIds)

  for (let i = 0; i < length; i++) {
    const item = cart.items[i]

    if (item.type === 'EVENT') {
      const line = buildEventCheckoutLine(item, {
        includePrice: true,
      }) as OrderCreationEventItem
      if (includeSupplierIds && item.supplierId) {
        line.supplier_id = item.supplierId
      }
      items.push(line)
    }

    if (item.type === 'GIFT_CARD') {
      const line = buildGiftCardCheckoutLine(item as GiftcardCartItem, {
        external,
        bookingSupplierFallback: bookingGiftcardSupplierFallback,
      }) as OrderCreationGiftCardItem
      if (includeSupplierIds && line.supplier_id === undefined) {
        line.supplier_id = KONFETTI_GIFTCARD_SUPPLIER_ID
      }
      items.push(line)
    }
  }

  const addons = getAllAddonsForOrder(cart)
  items.push(...addons)

  return items
}

const getCartItemUniqueId = (item: CartItem) => {
  if (isGiftcard(item)) {
    // For giftcards, compare multiple properties to ensure uniqueness
    return `${getGiftcardId(item)}-${item.eventDescriptionId}-${(item as any).giftcardType}`
  }

  const instant = item.instantBooking ? '-instant' : ''
  return `${getItemId(item)}${instant}`
}

export const cartGetters = {
  getTotals,
  getShippingPrice,
  getItems,
  getItemName,
  getItemAttendanceMode,
  getItemImage,
  getItemPrice,
  getFirstAddOnName,
  getAddOnPrice,
  getTotalAddOnPrice,
  getItemId,
  getGiftcardId,
  getGiftcardAddon,
  getGiftcardAddonList,
  getItemQty,
  getTotalItems,
  getDiscount,
  getAvailableStock,
  getCartHasItem,
  getCartHasBoxEvent,
  hasShippingCosts,
  getItemPermalink,
  getCartTotalPrice,
  getItemsForOrder,
  getGiftboxPrice,
  getGiftboxPriceAmount,
  getGiftcardAddonTotalPrice,
  getTotalGiftboxPriceInCart,
  getGiftcardAddonSinglePrice,
  isGiftcard,
  isValueBasedGiftcard,
  getGiftcardType,
  hasGiftcard,
  getItemsForGtm,
  getItemMainCategory,
  fnGetItemValidationRemovableReasons,
  fnGetCartItemsLength,
  isPrivate,
  getUnitItemPrice,
  getItemPriceAmount,
  getItemVariantForGtm,
  getItemIdForGtm,
  getEventDescriptionId,
  getAllAddonsForOrder,
  getItemAddon,
  requiresShippingAddress,
  hasPackagingCosts,
  hasEventAnyEventThatRequiresAddress,
  getTotalWithoutPackagingAndShipping,
  getItemPricingAppliedDiscount,
  getItemPricingBasePrice,
  getItemPricingTotalPrice,
  getCartItemUniqueId,
  getItemTotalPrice,
}
